// =================================== UPDATING DYNAMIC STATE ==================================================
export const dynamicHandle = (id, prop, value, setState) => {
  return setState((prev) => {
    const index = prev.findIndex((item) => item.id === id);
    if (index === -1) {
      return [...prev, { id, [prop]: value }];
    } else {
      const item = prev[index];
      let updatedValue;
      if (prop === "choice" || prop === "choice2" || prop === "matching") {
        // Check if value is an empty array
        if (Array.isArray(value) && value.length === 0) {
          updatedValue = []; // Set choice array to empty if value is []
        } else {
          // Check if item[prop] exists and is an array
          if (Array.isArray(item[prop])) {
            // Check if choice already contains object with provided name
            const existingIndex = item[prop].findIndex(
              (obj) => obj[prop] === value[prop]
            );
            if (existingIndex !== -1) {
              // Update value of existing object if found
              const updatedChoice = [...item[prop]];
              updatedChoice[existingIndex] = {
                ...updatedChoice[existingIndex],
                ...value,
              };
              updatedValue = updatedChoice;
            } else {
              // Add new object to choice array
              updatedValue = [...item[prop], value];
            }
          } else {
            // If item[prop] doesn't exist or is not an array, initialize it with the new value
            updatedValue = [value];
          }
        }
      } else {
        updatedValue = value;
      }
      return [
        ...prev.slice(0, index),
        { ...item, [prop]: updatedValue },
        ...prev.slice(index + 1),
      ];
    }
  });
};

// ============================================= RETURN DURATION =================================
export const calculateDuration = (value) => {
  if (value === "1 Hr") {
    return 1 * 60 * 60 * 1000;
  } else if (value === "2 Hrs") {
    return 2 * 60 * 60 * 1000;
  } else if (value === "3 Hrs") {
    return 3 * 60 * 60 * 1000;
  } else {
    return 30 * 60 * 1000;
  }
};

// ========================================== LATEST RESULT ======================================
export const calculateLatestTestResults = (results) => {
  // Handle empty or non-array input
  if (!Array.isArray(results) || results.length === 0) {
    return [
      {
        name: null,
        excel: 0,
        good: 0,
        average: 0,
        failed: 0,
      },
    ];
  }
  const name = results[0]?.name;
  // Use reduce for efficient calculation and object creation (assuming all elements have score and total)
  const testResults = results.reduce(
    (acc, result) => {
      const currentRatio = (result.score / result.total) * 100;
      if (currentRatio >= 90) {
        acc.excel++;
      } else if (currentRatio >= 70 && currentRatio < 90) {
        acc.good++;
      } else if (currentRatio >= 50 && currentRatio < 70) {
        acc.average++;
      } else {
        acc.failed++;
      }
      return acc;
    },
    { excel: 0, good: 0, average: 0, failed: 0 }
  );
  return [
    {
      name,
      ...testResults,
    },
  ];
};

// ========================================== SCORE ANALYSIS ======================================
export const calculateScoreAnalysis = (results) => {
  // Handle empty or non-array input
  if (!Array.isArray(results) || results.length === 0) {
    return [
      { name: "excel", value: 0 },
      { name: "good", value: 0 },
      { name: "average", value: 0 },
      { name: "failed", value: 0 },
    ];
  }
  // Use reduce for efficient calculation
  const testResults = results.reduce(
    (acc, result) => {
      const currentRatio = (result.score / result.total) * 100;
      if (currentRatio >= 90) {
        acc.excel++;
      } else if (currentRatio >= 70 && currentRatio < 90) {
        acc.good++;
      } else if (currentRatio >= 50 && currentRatio < 70) {
        acc.average++;
      } else {
        acc.failed++;
      }
      return acc;
    },
    { excel: 0, good: 0, average: 0, failed: 0 }
  );
  // Update values in the returned array
  return [
    { name: "excel", value: testResults.excel },
    { name: "good", value: testResults.good },
    { name: "average", value: testResults.average },
    { name: "failed", value: testResults.failed },
  ];
};

// ================================================= FILTER DATA BASED ON YEAR ==============================
export const filterDataBasedOnYear = (results, year) => {
  return results.filter(
    (item) => item?.Test?.scheduled_at.split("-")[0] === year
  );
};
// ========================================== FILTER BASED ON YEAR AND HALF ==============================
export const filterDataBasedOnYearAndHalf = (
  results,
  year,
  halfYear = "all"
) => {
  return results.filter((item) => {
    if (!item?.Test?.scheduled_at) {
      return false; // Handle missing data gracefully
    }

    const scheduledYear = parseInt(item.Test.scheduled_at.split("-")[0], 10);
    const month = parseInt(item.Test.scheduled_at.split("-")[1], 10);

    if (scheduledYear !== year) {
      return false; // Filter by year
    }

    if (halfYear === "first") {
      return month <= 6; // Filter for first half of the year
    } else if (halfYear === "second") {
      return month > 6; // Filter for second half of the year
    }

    // Return true for all conditions met (year and optional half-year)
    return true;
  });
};
// =================================== FILTER BASED ON CURRENT YEAR AND CURRENT MONTH ============================
export const filterDataBasedOnCurrentYearAndMonth = (results) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // Months are zero-indexed

  return results.filter((item) => {
    if (!item?.Test?.scheduled_at) {
      return false; // Handle missing data gracefully
    }
    const [yearString, monthString] = item.Test.scheduled_at.split("-");
    const year = parseInt(yearString, 10);
    const month = parseInt(monthString, 10);

    return year === currentYear && month === currentMonth;
  });
};

// ================================================== GET UNIQUE TEST NAME ===============================
export const getUniqueTestNames = (results) => {
  const testNames = new Set();
  for (const result of results) {
    testNames.add(result.Test.name);
  }
  return Array.from(testNames);
};
//  ================================================ REPORT COLUMNS =====================================
export const reportColumns = (data, isMdUp) => {
  const columns = [
    {
      field: "sl",
      headerName: "Sl. No",
      flex: isMdUp ? 40 : undefined,
      width: isMdUp ? undefined : 40,
    },
    {
      field: "name",
      headerName: "Name",
      flex: isMdUp ? 160 : undefined,
      width: isMdUp ? undefined : 160,
    },
    {
      field: "region",
      headerName: "Region",
      flex: isMdUp ? 160 : undefined,
      width: isMdUp ? undefined : 160,
    },
    {
      field: "empId",
      headerName: "Employee ID",
      flex: isMdUp ? 130 : undefined,
      width: isMdUp ? undefined : 130,
    },
    {
      field: "ccs",
      headerName: "Customer Care & Service",
      flex: isMdUp ? 200 : undefined,
      width: isMdUp ? undefined : 200,
    },
    {
      field: "pksl",
      headerName: "Product Knowledge & Self Learning",
      flex: isMdUp ? 250 : undefined,
      width: isMdUp ? undefined : 250,
    },
  ];
  // Loop through data and insert test columns
  for (let i = 0; i < data.length; i++) {
    columns.splice(i + 4, 0, {
      field: data[i],
      headerName: data[i],
      flex: isMdUp ? 100 : undefined,
      width: isMdUp ? undefined : 150,
    });
  }

  return columns;
};

// ================================================= YEARLY REPORT ==========================================
export const yearlyReport = (results, users) => {
  const output = [];
  const userMap = {};

  // Assign unique IDs while creating user objects
  for (const result of results) {
    const {
      User: { name, empId, id: userId, Region: region, isAdmin },
      Test: { name: testName },
      score,
      total,
    } = result;

    // Skip users with isAdmin === true
    if (isAdmin) continue;

    if (!userMap[userId]) {
      userMap[userId] = {
        id: userId,
        name,
        empId,
        region: region?.region || "",
        totalScore: 0,
        totalMark: 0,
      };
    }
    userMap[userId][testName] = parseFloat((score / total) * 100).toFixed(2);
    userMap[userId].totalScore += score;
    userMap[userId].totalMark += total;
  }

  // Add users who did not appear for any test and have isAdmin === false
  const filteredUsers = users.filter((user) => !user.isAdmin);

  for (const user of filteredUsers) {
    const { id: userId, name, empId, Region: region } = user;

    if (!userMap[userId]) {
      userMap[userId] = {
        id: userId,
        name,
        empId,
        region: region?.region || "",
        totalScore: 0,
        totalMark: 0,
      };
    }
  }

  // Calculate CCS and PSKL based on total score and mark
  for (const userId in userMap) {
    const user = userMap[userId];
    user.ccs = parseFloat(
      (user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 5
    ).toFixed(2);
    user.pksl = parseFloat(
      (user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 2 + 3
    ).toFixed(2);
  }

  // Convert userMap values to desired output format
  for (const userId in userMap) {
    output.push(userMap[userId]);
  }

  return output;
};

// export const yearlyReport = (results, users) => {
//   const output = [];
//   const userMap = {};

//   // Assign unique IDs while creating user objects
//   for (const result of results) {
//     const {
//       User: { name, empId, id: userId, Region: region, isAdmin },
//       Test: { name: testName },
//       score,
//       total,
//     } = result;

//     // Skip users with role.admin === true
//     if (isAdmin) continue;

//     if (!userMap[name]) {
//       userMap[name] = {
//         id: userId,
//         name,
//         empId,
//         region: region?.region || "Unknown",
//         totalScore: 0,
//         totalMark: 0,
//       };
//     }
//     userMap[name][testName] = parseFloat((score / total) * 100).toFixed(2);
//     userMap[name].totalScore += score;
//     userMap[name].totalMark += total;
//   }

//   // Add users who did not appear for any test and have role.admin === false
//   const filteredUsers = users.filter((user) => !user.isAdmin);

//   for (const user of filteredUsers) {
//     const { name, empId, id: userId, Region: region } = user;

//     if (!userMap[name]) {
//       userMap[name] = {
//         id: userId,
//         name,
//         empId,
//         region: region?.region || "Unknown",
//         totalScore: 0,
//         totalMark: 0,
//       };
//     }
//   }

//   // Calculate CCS and PSKL based on total score and mark
//   for (const userName in userMap) {
//     const user = userMap[userName];
//     user.ccs = parseFloat((user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 5).toFixed(2);
//     user.pksl = parseFloat((user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 2 + 3).toFixed(2);
//   }

//   // Convert userMap values to desired output format
//   for (const userName in userMap) {
//     output.push(userMap[userName]);
//   }

//   return output;
// };

// ================================================= YEARLY REPORT ==========================================
export const yearlyReportForAUser = (results, region) => {
  const output = [];
  const userMap = {};

  // Assign unique IDs while creating user objects
  for (const result of results) {
    const {
      User: user,
      Test: { name: testName },
      score,
      total,
    } = result;
    const { name, empId } = user;
    const userId = result?.User?.id;

    if (!userMap[name]) {
      userMap[name] = {
        id: userId,
        name,
        empId,
        region: region || "",
        totalScore: 0,
        totalMark: 0,
      };
    }
    userMap[name][testName] = parseFloat((score / total) * 100).toFixed(2);
    userMap[name].totalScore += score;
    userMap[name].totalMark += total;
  }

  // Calculate CCS and PSKL based on total score and mark
  for (const userName in userMap) {
    const user = userMap[userName];
    user.ccs = parseFloat(
      (user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 5
    ).toFixed(2);
    user.pksl = parseFloat(
      (user.totalMark > 0 ? user.totalScore / user.totalMark : 0) * 2 + 3
    ).toFixed(2);
  }

  // Convert userMap values to desired output format
  for (const userName in userMap) {
    output.push(userMap[userName]);
  }

  return output;
};
// ================================================================== USER YEAR GRAPH DATA ==============================
export const userYearGraphData = (results) => {
  return results?.map((item) => ({
    name: item?.Test?.name,
    score: parseFloat((item?.score / item?.total) * 100).toFixed(2),
  }));
};

//  ===========================================================  REARRANGE ARRAY ITEMS ==================================
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// =================================================== CUSTOM ISO STRING FORMAT ==========================
export const customISOString = (date) => {
  const pad = (n, z = 2) => String(n).padStart(z, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};

// ==================================================== CALCULATE TIME TAKEN TO TAKE TEST =======================================
export const calculateDurationTaken = (elapsedMs) => {
  const hours = Math.floor(elapsedMs / 3600000);
  const minutes = Math.floor((elapsedMs % 3600000) / 60000);
  const seconds = Math.floor((elapsedMs % 60000) / 1000);
  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  return formattedTime;
};

//  ================================================ ANSWERS REPORT COLUMNS =====================================
export const answersReportColumns = () => {
  const columns = [
    {
      field: "sl",
      headerName: "Sl. No",
      width: 40,
    },
    {
      field: "testName",
      headerName: "Test Name",
      width: 200,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "empId",
      headerName: "Employee ID",
      width: 100,
    },
    {
      field: "question",
      headerName: "Question",
      width: 1000,
    },
    {
      field: "questionType",
      headerName: "Question Type",
      width: 250,
    },
    {
      field: "optionOne",
      headerName: "Option One",
      width: 350,
    },
    {
      field: "optionTwo",
      headerName: "Option Two",
      width: 350,
    },
    {
      field: "answer",
      headerName: "Answers",
      width: 250,
    },
  ];
  return columns;
};

// ========================================== SAFE USER STORAGE ==================================
export const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    if (!userStr || userStr === "undefined" || userStr === "null") {
      return null;
    }
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    try {
      localStorage.removeItem("user");
    } catch (_) {}
    return null;
  }
};

export const setStoredUser = (user) => {
  try {
    if (user && typeof user === "object") {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  } catch (error) {
    console.error("Error setting user in localStorage:", error);
  }
};
